import React, { useState, useEffect } from 'react';
import { Select, Button, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { surgeryService } from '../../services/surgeryService';
import { patientService } from '../../services';
import './Surgery.css';

function Surgery() {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('requests');
    const [requests, setRequests] = useState([]);
    const [calendar, setCalendar] = useState([]);
    const [allSurgeries, setAllSurgeries] = useState([]);
    const [patients, setPatients] = useState([]);
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [showSurgeryModal, setShowSurgeryModal] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [selectedSurgery, setSelectedSurgery] = useState(null);
    const [requestForm, setRequestForm] = useState({
        patientId: '',
        surgeryType: '',
        diagnosis: '',
        urgencyLevel: 'elective',
        preferredDate: '',
        estimatedDuration: '',
        anesthesiaType: '',
        specialRequirements: ''
    });
    const [scheduleForm, setScheduleForm] = useState({
        scheduledDate: '',
        scheduledTime: '',
        operatingRoom: '',
        surgeonId: '',
        anesthesiologistId: ''
    });
    const [completionForm, setCompletionForm] = useState({
        complications: '',
        surgicalNotes: '',
        postOpInstructions: ''
    });

    useEffect(() => {
        loadRequests();
        loadCalendar();
        loadAllSurgeries();
        loadPatients();
    }, []);

    const loadPatients = async () => {
        try {
            const response = await patientService.getPatients();
            setPatients(response.data || []);
        } catch (error) {
            console.error('Error loading patients:', error);
        }
    };

    const loadRequests = async () => {
        try {
            const data = await surgeryService.getRequests();
            setRequests(data);
        } catch (error) {
            console.error('Error loading requests:', error);
        }
    };

    const loadCalendar = async () => {
        try {
            const startDate = new Date();
            const endDate = new Date();
            endDate.setMonth(endDate.getMonth() + 1);
            const data = await surgeryService.getCalendar(
                startDate.toISOString().split('T')[0],
                endDate.toISOString().split('T')[0]
            );
            setCalendar(data);
        } catch (error) {
            console.error('Error loading calendar:', error);
        }
    };

    const loadAllSurgeries = async () => {
        try {
            const startDate = new Date();
            startDate.setFullYear(startDate.getFullYear() - 1); // Last year
            const endDate = new Date();
            endDate.setMonth(endDate.getMonth() + 1);
            const data = await surgeryService.getCalendar(
                startDate.toISOString().split('T')[0],
                endDate.toISOString().split('T')[0]
            );
            setAllSurgeries(data);
        } catch (error) {
            console.error('Error loading all surgeries:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await surgeryService.createRequest(requestForm);
            message.success('Surgery request created successfully!');
            setShowRequestModal(false);
            setRequestForm({
                patientId: '',
                surgeryType: '',
                diagnosis: '',
                urgencyLevel: 'elective',
                preferredDate: '',
                estimatedDuration: '',
                anesthesiaType: '',
                specialRequirements: ''
            });
            loadRequests();
        } catch (error) {
            message.error('Error creating request: ' + error.message);
        }
    };

    const handleViewRequest = (request) => {
        setSelectedRequest(request);
        setShowDetailsModal(true);
    };

    const handleViewSurgery = (surgery) => {
        setSelectedSurgery(surgery);
        setShowSurgeryModal(true);
    };

    const handleApprove = async (requestId) => {
        try {
            await surgeryService.approveRequest(requestId, {
                approvalType: 'medical',
                status: 'approved',
                comments: 'Approved by doctor'
            });
            message.success('Surgery request approved! You can now schedule it.');
            setShowDetailsModal(false);
            loadRequests();
        } catch (error) {
            message.error('Error approving request: ' + error.message);
        }
    };

    const handleSchedule = async (e) => {
        e.preventDefault();
        try {
            const scheduleData = {
                scheduledDate: scheduleForm.scheduledDate,
                scheduledTime: scheduleForm.scheduledTime,
                surgeonId: selectedRequest.requestingDoctor?._id || selectedRequest.requestingDoctor
            };

            // Only add optional fields if they have values
            if (scheduleForm.operatingRoom) {
                scheduleData.operatingRoom = scheduleForm.operatingRoom;
            }
            if (scheduleForm.anesthesiologistId) {
                scheduleData.anesthesiologistId = scheduleForm.anesthesiologistId;
            }

            await surgeryService.scheduleSurgery(selectedRequest._id, scheduleData);
            message.success('Surgery scheduled successfully! Check the Surgery Calendar tab.');
            setShowScheduleModal(false);
            setShowDetailsModal(false);
            setScheduleForm({
                scheduledDate: '',
                scheduledTime: '',
                operatingRoom: '',
                surgeonId: '',
                anesthesiologistId: ''
            });
            await loadRequests();
            await loadCalendar();
            setActiveTab('calendar');
        } catch (error) {
            message.error('Error scheduling surgery: ' + error.message);
        }
    };

    const openScheduleModal = () => {
        setShowDetailsModal(false);
        setShowScheduleModal(true);
    };

    const handleStartSurgery = async (surgeryId) => {
        try {
            await surgeryService.startSurgery(surgeryId);
            message.success('Surgery marked as in progress!');
            setShowSurgeryModal(false);
            await loadCalendar();
        } catch (error) {
            message.error('Error starting surgery: ' + error.message);
        }
    };

    const handleCompleteSurgery = async (e) => {
        e.preventDefault();
        try {
            await surgeryService.completeSurgery(selectedSurgery._id, completionForm);
            message.success('Surgery marked as completed!');
            setShowCompletionModal(false);
            setShowSurgeryModal(false);
            setCompletionForm({
                complications: '',
                surgicalNotes: '',
                postOpInstructions: ''
            });
            await loadCalendar();
            await loadRequests();
        } catch (error) {
            message.error('Error completing surgery: ' + error.message);
        }
    };

    const openCompletionModal = () => {
        setShowSurgeryModal(false);
        setShowCompletionModal(true);
    };

    const getUrgencyClass = (level) => {
        const classes = {
            emergency: 'urgency-emergency',
            urgent: 'urgency-urgent',
            elective: 'urgency-elective'
        };
        return classes[level] || '';
    };

    const getStatusClass = (status) => {
        const classes = {
            pending: 'status-pending',
            approved: 'status-approved',
            rejected: 'status-rejected',
            scheduled: 'status-scheduled',
            completed: 'status-completed',
            cancelled: 'status-cancelled'
        };
        return classes[status] || '';
    };

    return (
        <div className="surgery-container">
            <div className="page-header">
                <h1>{t('surgeryManagement')}</h1>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setShowRequestModal(true)}
                    size="large"
                >
                    {t('createSurgeryRequest')}
                </Button>
            </div>

            <div className="tabs">
                <button
                    className={activeTab === 'requests' ? 'tab active' : 'tab'}
                    onClick={() => setActiveTab('requests')}
                >
                    {t('surgeryRequests')} ({requests.length})
                </button>
                <button
                    className={activeTab === 'calendar' ? 'tab active' : 'tab'}
                    onClick={() => setActiveTab('calendar')}
                >
                    {t('surgeryCalendar')}
                </button>
                <button
                    className={activeTab === 'reports' ? 'tab active' : 'tab'}
                    onClick={() => setActiveTab('reports')}
                >
                    Reports
                </button>
            </div>

            {activeTab === 'requests' && (
                <div className="requests-content">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>{t('date')}</th>
                                <th>{t('patient')}</th>
                                <th>{t('surgeryType')}</th>
                                <th>{t('diagnosis')}</th>
                                <th>{t('urgency')}</th>
                                <th>{t('preferredDate')}</th>
                                <th>{t('status')}</th>
                                <th>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map((req) => (
                                <tr key={req._id}>
                                    <td>{new Date(req.createdAt).toLocaleDateString()}</td>
                                    <td>{req.patient?.name}</td>
                                    <td>{req.surgeryType}</td>
                                    <td>{req.diagnosis}</td>
                                    <td>
                                        <span className={`urgency-badge ${getUrgencyClass(req.urgencyLevel)}`}>
                                            {req.urgencyLevel}
                                        </span>
                                    </td>
                                    <td>{req.preferredDate ? new Date(req.preferredDate).toLocaleDateString() : '-'}</td>
                                    <td>
                                        <span className={`status-badge ${getStatusClass(req.status)}`}>
                                            {req.status}
                                        </span>
                                    </td>
                                    <td>
                                        <button className="btn-sm" onClick={() => handleViewRequest(req)}>View</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {requests.length === 0 && (
                        <div className="no-data">No surgery requests yet</div>
                    )}
                </div>
            )}

            {activeTab === 'calendar' && (
                <div className="calendar-content">
                    <div className="calendar-list">
                        {calendar.map((surgery) => {
                            const surgeryReq = surgery.surgeryRequest;
                            return (
                                <div key={surgery._id} className="calendar-item">
                                    <div className="calendar-date">
                                        <div className="date-day">{new Date(surgery.scheduledDate).getDate()}</div>
                                        <div className="date-month">{new Date(surgery.scheduledDate).toLocaleDateString('en-US', { month: 'short' })}</div>
                                    </div>
                                    <div className="calendar-details">
                                        <h3>{surgeryReq?.patient?.name || 'Unknown Patient'}</h3>
                                        <p><strong>Surgery:</strong> {surgeryReq?.surgeryType || 'N/A'}</p>
                                        <p><strong>Time:</strong> {surgery.scheduledTime}</p>
                                        <p><strong>OR:</strong> {surgery.operatingRoom || 'TBD'}</p>
                                        <p><strong>Surgeon:</strong> Dr. {surgery.surgeon?.name || 'Assigned'}</p>
                                        <span className={`status-badge ${getStatusClass(surgery.status)}`}>
                                            {surgery.status}
                                        </span>
                                    </div>
                                    <div className="calendar-actions">
                                        <button className="btn-sm" onClick={() => handleViewSurgery(surgery)}>View Details</button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {calendar.length === 0 && (
                        <div className="no-data">
                            <p>No scheduled surgeries yet</p>
                            <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '10px' }}>Approve and schedule surgery requests to see them here</p>
                        </div>
                    )}
                </div>
            )}

            {/* Reports Tab */}
            {activeTab === 'reports' && (
                <div className="reports-content">
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: '#e3f2fd' }}>
                                <span style={{ fontSize: '2rem' }}>📋</span>
                            </div>
                            <div className="stat-info">
                                <h3>{allSurgeries.length}</h3>
                                <p>Total Surgeries</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: '#fff3e0' }}>
                                <span style={{ fontSize: '2rem' }}>⏰</span>
                            </div>
                            <div className="stat-info">
                                <h3>{allSurgeries.filter(s => s.status === 'scheduled').length}</h3>
                                <p>Scheduled</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: '#e8f5e9' }}>
                                <span style={{ fontSize: '2rem' }}>✅</span>
                            </div>
                            <div className="stat-info">
                                <h3>{allSurgeries.filter(s => s.status === 'completed').length}</h3>
                                <p>Completed</p>
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon" style={{ background: '#fff9c4' }}>
                                <span style={{ fontSize: '2rem' }}>🔄</span>
                            </div>
                            <div className="stat-info">
                                <h3>{allSurgeries.filter(s => s.status === 'in_progress').length}</h3>
                                <p>In Progress</p>
                            </div>
                        </div>
                    </div>

                    <div className="report-section">
                        <h2>Surgery History</h2>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Patient</th>
                                    <th>Surgery Type</th>
                                    <th>Diagnosis</th>
                                    <th>OR</th>
                                    <th>Duration</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allSurgeries.slice(0, 20).map((surgery) => {
                                    const surgeryReq = surgery.surgeryRequest;
                                    let duration = 'N/A';
                                    if (surgery.actualStartTime && surgery.actualEndTime) {
                                        const diff = new Date(surgery.actualEndTime) - new Date(surgery.actualStartTime);
                                        const hours = Math.floor(diff / 3600000);
                                        const minutes = Math.floor((diff % 3600000) / 60000);
                                        duration = `${hours}h ${minutes}m`;
                                    }
                                    return (
                                        <tr key={surgery._id}>
                                            <td>{new Date(surgery.scheduledDate).toLocaleDateString()}</td>
                                            <td>{surgeryReq?.patient?.name || 'N/A'}</td>
                                            <td>{surgeryReq?.surgeryType || 'N/A'}</td>
                                            <td>{surgeryReq?.diagnosis || 'N/A'}</td>
                                            <td>{surgery.operatingRoom || 'TBD'}</td>
                                            <td>{duration}</td>
                                            <td>
                                                <span className={`status-badge ${getStatusClass(surgery.status)}`}>
                                                    {surgery.status}
                                                </span>
                                            </td>
                                            <td>
                                                <button className="btn-sm" onClick={() => handleViewSurgery(surgery)}>View</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {allSurgeries.length === 0 && (
                            <div className="no-data">
                                <p>No surgery history yet</p>
                            </div>
                        )}
                    </div>

                    {allSurgeries.filter(s => s.status === 'completed').length > 0 && (
                        <div className="report-section">
                            <h2>Completed Surgeries Details</h2>
                            <div className="completed-list">
                                {allSurgeries.filter(s => s.status === 'completed').slice(0, 10).map((surgery) => {
                                    const surgeryReq = surgery.surgeryRequest;
                                    return (
                                        <div key={surgery._id} className="completed-surgery-card">
                                            <div className="completed-header">
                                                <div>
                                                    <h3>{surgeryReq?.patient?.name}</h3>
                                                    <p className="surgery-type">{surgeryReq?.surgeryType}</p>
                                                </div>
                                                <div className="completed-date">
                                                    {new Date(surgery.scheduledDate).toLocaleDateString()}
                                                </div>
                                            </div>
                                            {surgery.surgicalNotes && (
                                                <div className="surgery-notes">
                                                    <strong>Surgical Notes:</strong>
                                                    <p>{surgery.surgicalNotes}</p>
                                                </div>
                                            )}
                                            {surgery.complications && (
                                                <div className="surgery-complications">
                                                    <strong>Complications:</strong>
                                                    <p>{surgery.complications}</p>
                                                </div>
                                            )}
                                            {surgery.postOpInstructions && (
                                                <div className="surgery-instructions">
                                                    <strong>Post-Op Instructions:</strong>
                                                    <p>{surgery.postOpInstructions}</p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Request Details Modal */}
            {showDetailsModal && selectedRequest && (
                <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
                    <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                        <h2>Surgery Request Details</h2>
                        <div className="details-grid">
                            <div className="detail-item">
                                <label>Patient:</label>
                                <p>{selectedRequest.patient?.name}</p>
                            </div>
                            <div className="detail-item">
                                <label>Surgery Type:</label>
                                <p>{selectedRequest.surgeryType}</p>
                            </div>
                            <div className="detail-item">
                                <label>Diagnosis:</label>
                                <p>{selectedRequest.diagnosis}</p>
                            </div>
                            <div className="detail-item">
                                <label>Urgency Level:</label>
                                <span className={`urgency-badge ${getUrgencyClass(selectedRequest.urgencyLevel)}`}>
                                    {selectedRequest.urgencyLevel}
                                </span>
                            </div>
                            <div className="detail-item">
                                <label>Preferred Date:</label>
                                <p>{selectedRequest.preferredDate ? new Date(selectedRequest.preferredDate).toLocaleDateString() : 'Not specified'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Estimated Duration:</label>
                                <p>{selectedRequest.estimatedDuration ? `${selectedRequest.estimatedDuration} minutes` : 'Not specified'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Anesthesia Type:</label>
                                <p>{selectedRequest.anesthesiaType || 'Not specified'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Status:</label>
                                <span className={`status-badge ${getStatusClass(selectedRequest.status)}`}>
                                    {selectedRequest.status}
                                </span>
                            </div>
                            {selectedRequest.specialRequirements && (
                                <div className="detail-item full-width">
                                    <label>Special Requirements:</label>
                                    <p>{selectedRequest.specialRequirements}</p>
                                </div>
                            )}
                        </div>
                        <div className="modal-actions">
                            <Button onClick={() => setShowDetailsModal(false)}>Close</Button>
                            {selectedRequest.status === 'pending' && (
                                <Button type="primary" onClick={() => handleApprove(selectedRequest._id)}>
                                    Approve Request
                                </Button>
                            )}
                            {selectedRequest.status === 'approved' && (
                                <Button type="primary" onClick={openScheduleModal}>
                                    Schedule Surgery
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Schedule Surgery Modal */}
            {showScheduleModal && selectedRequest && (
                <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Schedule Surgery</h2>
                        <form onSubmit={handleSchedule}>
                            <div className="form-group">
                                <label>Scheduled Date *</label>
                                <input
                                    type="date"
                                    value={scheduleForm.scheduledDate}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, scheduledDate: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Scheduled Time *</label>
                                <input
                                    type="time"
                                    value={scheduleForm.scheduledTime}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, scheduledTime: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Operating Room</label>
                                <input
                                    type="text"
                                    value={scheduleForm.operatingRoom}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, operatingRoom: e.target.value })}
                                    placeholder="e.g., OR-1, OR-2"
                                />
                            </div>
                            <div className="modal-actions">
                                <Button onClick={() => {
                                    setShowScheduleModal(false);
                                    setShowDetailsModal(true);
                                }}>
                                    Back
                                </Button>
                                <Button type="primary" htmlType="submit">
                                    Schedule Surgery
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* Create Request Modal */}
            {showRequestModal && (
                <div className="modal-overlay" onClick={() => setShowRequestModal(false)}>
                    <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('createSurgeryRequest')}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('patient')} *</label>
                                    <Select
                                        showSearch
                                        value={requestForm.patientId || undefined}
                                        onChange={(value) => setRequestForm({ ...requestForm, patientId: value })}
                                        placeholder={t('searchAndSelectPatient')}
                                        optionFilterProp="label"
                                        style={{ width: '100%' }}
                                        required
                                    >
                                        {patients.map((patient) => (
                                            <Select.Option
                                                key={patient._id}
                                                value={patient._id}
                                                label={`${patient.name} - ${patient.phoneNumber}`}
                                            >
                                                {patient.name} - {patient.phoneNumber}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="form-group">
                                    <label>{t('urgencyLevel')} *</label>
                                    <select
                                        value={requestForm.urgencyLevel}
                                        onChange={(e) => setRequestForm({ ...requestForm, urgencyLevel: e.target.value })}
                                    >
                                        <option value="elective">{t('elective')}</option>
                                        <option value="urgent">{t('urgent')}</option>
                                        <option value="emergency">{t('emergency')}</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{t('surgeryType')} *</label>
                                <input
                                    type="text"
                                    value={requestForm.surgeryType}
                                    onChange={(e) => setRequestForm({ ...requestForm, surgeryType: e.target.value })}
                                    required
                                    placeholder={t('surgeryTypePlaceholder')}
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('diagnosis')} *</label>
                                <textarea
                                    value={requestForm.diagnosis}
                                    onChange={(e) => setRequestForm({ ...requestForm, diagnosis: e.target.value })}
                                    required
                                    rows="2"
                                    placeholder={t('diagnosisPlaceholder')}
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('preferredDate')}</label>
                                    <input
                                        type="date"
                                        value={requestForm.preferredDate}
                                        onChange={(e) => setRequestForm({ ...requestForm, preferredDate: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>{t('estimatedDuration')} ({t('minutes')})</label>
                                    <input
                                        type="number"
                                        value={requestForm.estimatedDuration}
                                        onChange={(e) => setRequestForm({ ...requestForm, estimatedDuration: e.target.value })}
                                        min="0"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>{t('anesthesiaType')}</label>
                                <input
                                    type="text"
                                    value={requestForm.anesthesiaType}
                                    onChange={(e) => setRequestForm({ ...requestForm, anesthesiaType: e.target.value })}
                                    placeholder={t('anesthesiaPlaceholder')}
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('specialRequirements')}</label>
                                <textarea
                                    value={requestForm.specialRequirements}
                                    onChange={(e) => setRequestForm({ ...requestForm, specialRequirements: e.target.value })}
                                    rows="3"
                                    placeholder={t('specialRequirementsPlaceholder')}
                                />
                            </div>

                            <div className="modal-actions">
                                <Button onClick={() => setShowRequestModal(false)}>
                                    {t('cancel')}
                                </Button>
                                <Button type="primary" htmlType="submit">
                                    {t('createRequest')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Surgery Details Modal (for calendar items) */}
            {showSurgeryModal && selectedSurgery && (
                <div className="modal-overlay" onClick={() => setShowSurgeryModal(false)}>
                    <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                        <h2>Surgery Details</h2>
                        <div className="details-grid">
                            <div className="detail-item">
                                <label>Patient:</label>
                                <p>{selectedSurgery.surgeryRequest?.patient?.name || 'N/A'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Phone:</label>
                                <p>{selectedSurgery.surgeryRequest?.patient?.phone || 'N/A'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Surgery Type:</label>
                                <p>{selectedSurgery.surgeryRequest?.surgeryType || 'N/A'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Diagnosis:</label>
                                <p>{selectedSurgery.surgeryRequest?.diagnosis || 'N/A'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Scheduled Date:</label>
                                <p>{new Date(selectedSurgery.scheduledDate).toLocaleDateString()}</p>
                            </div>
                            <div className="detail-item">
                                <label>Scheduled Time:</label>
                                <p>{selectedSurgery.scheduledTime}</p>
                            </div>
                            <div className="detail-item">
                                <label>Operating Room:</label>
                                <p>{selectedSurgery.operatingRoom || 'TBD'}</p>
                            </div>
                            <div className="detail-item">
                                <label>Surgeon:</label>
                                <p>Dr. {selectedSurgery.surgeon?.name || 'Assigned'}</p>
                            </div>
                            {selectedSurgery.anesthesiologist && (
                                <div className="detail-item">
                                    <label>Anesthesiologist:</label>
                                    <p>Dr. {selectedSurgery.anesthesiologist.name}</p>
                                </div>
                            )}
                            <div className="detail-item">
                                <label>Status:</label>
                                <span className={`status-badge ${getStatusClass(selectedSurgery.status)}`}>
                                    {selectedSurgery.status}
                                </span>
                            </div>
                            {selectedSurgery.surgeryRequest?.estimatedDuration && (
                                <div className="detail-item">
                                    <label>Estimated Duration:</label>
                                    <p>{selectedSurgery.surgeryRequest.estimatedDuration} minutes</p>
                                </div>
                            )}
                            {selectedSurgery.surgeryRequest?.anesthesiaType && (
                                <div className="detail-item">
                                    <label>Anesthesia Type:</label>
                                    <p>{selectedSurgery.surgeryRequest.anesthesiaType}</p>
                                </div>
                            )}
                            {selectedSurgery.surgeryRequest?.specialRequirements && (
                                <div className="detail-item full-width">
                                    <label>Special Requirements:</label>
                                    <p>{selectedSurgery.surgeryRequest.specialRequirements}</p>
                                </div>
                            )}
                        </div>
                        <div className="modal-actions">
                            <Button onClick={() => setShowSurgeryModal(false)}>Close</Button>
                            {selectedSurgery.status === 'scheduled' && (
                                <Button type="primary" onClick={() => handleStartSurgery(selectedSurgery._id)}>
                                    Start Surgery
                                </Button>
                            )}
                            {selectedSurgery.status === 'in_progress' && (
                                <Button type="primary" onClick={openCompletionModal}>
                                    Complete Surgery
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Complete Surgery Modal */}
            {showCompletionModal && selectedSurgery && (
                <div className="modal-overlay" onClick={() => setShowCompletionModal(false)}>
                    <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                        <h2>Complete Surgery</h2>
                        <p style={{ color: '#666', marginBottom: '20px' }}>
                            Patient: <strong>{selectedSurgery.surgeryRequest?.patient?.name}</strong> |
                            Surgery: <strong>{selectedSurgery.surgeryRequest?.surgeryType}</strong>
                        </p>
                        <form onSubmit={handleCompleteSurgery}>
                            <div className="form-group">
                                <label>Surgical Notes *</label>
                                <textarea
                                    value={completionForm.surgicalNotes}
                                    onChange={(e) => setCompletionForm({ ...completionForm, surgicalNotes: e.target.value })}
                                    rows="4"
                                    required
                                    placeholder="Describe the surgical procedure, findings, and techniques used..."
                                />
                            </div>

                            <div className="form-group">
                                <label>Complications</label>
                                <textarea
                                    value={completionForm.complications}
                                    onChange={(e) => setCompletionForm({ ...completionForm, complications: e.target.value })}
                                    rows="3"
                                    placeholder="Note any complications encountered (or leave empty if none)..."
                                />
                            </div>

                            <div className="form-group">
                                <label>Post-Operative Instructions *</label>
                                <textarea
                                    value={completionForm.postOpInstructions}
                                    onChange={(e) => setCompletionForm({ ...completionForm, postOpInstructions: e.target.value })}
                                    rows="4"
                                    required
                                    placeholder="Provide recovery instructions, follow-up care, medications, and restrictions..."
                                />
                            </div>

                            <div className="modal-actions">
                                <Button onClick={() => {
                                    setShowCompletionModal(false);
                                    setShowSurgeryModal(true);
                                }}>
                                    Back
                                </Button>
                                <Button type="primary" htmlType="submit">
                                    Mark as Completed
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Surgery;
